import { showAlert } from "components/molecules/Alert/Alert";
import i18n from "locales/i18n";

/**
 * Displays a SweetAlert notification indicating an invalid password.
 * This function triggers a modal alert with a message for the user when the
 * password they have entered is invalid, using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showSweetAlertInvalidPassword = () => {
  showAlert({
    text: i18n.t("changePassword.message3"),
    icon: "info",
  });
};

/**
 * Displays a SweetAlert notification indicating that passwords do not match.
 * This function triggers a modal alert with a message for the user when the
 * entered passwords do not match, using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showSweetAlertMatch = () => {
  showAlert({
    text: i18n.t("changePassword.message1"),
    icon: "error",
  });
};

/**
 * Displays a SweetAlert notification indicating a successful password reset.
 * This function triggers a modal alert with a success message for the user
 * after the password has been successfully reset, using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showSweetAlertSuccess = () => {
  showAlert({
    text: i18n.t("forgotPassword.successfullReset"),
  });
};

/**
 * Displays a SweetAlert notification indicating a successful password change.
 * This function triggers a modal alert with a message for the user after they have
 * successfully changed their password, using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showSweetAlertSuccessNewPassword = () => {
  showAlert({
    text: i18n.t("changePassword.message2"),
  });
};

/**
 * Displays a SweetAlert notification indicating a service error during data submission.
 * This function triggers a modal alert with an error message when there is a failure
 * in submitting data to the server, using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertServiceError = () => {
  showAlert({
    text: i18n.t("error.data.submit"),
    icon: "error",
  });
};

/**
 * Displays a SweetAlert notification indicating that no forms were found.
 * This function triggers a modal alert with a warning message when no forms are found
 * in the search. After the alert is dismissed, it resets the search input and pagination.
 * @function
 * @param {Function} setSearch - Function to reset the search query.
 * @param {Function} setPagination - Function to update the pagination state.
 * @param {Object} pagination - The current pagination state object.
 * @returns {void} Does not return any value.
 */
export const showAlertNoFormsFound = (setSearch, setPagination, pagination) => {
  showAlert({
    text: i18n.t("filterQuery.NoFormsMessage"),
    icon: "info",
  }).then(() => {
    setSearch("");
    setPagination({ ...pagination, page: 1 });
  });
};

/**
 * Displays a SweetAlert notification indicating an error with the responsible user service.
 * This function triggers a modal alert with an error message corresponding to a specific
 * service error code (50064), using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertServiceResponsibleUser = () => {
  showAlert({
    text: i18n.t("50064"),
    icon: "error",
  });
};

/**
 * Displays a SweetAlert notification indicating that no forms are available.
 * This function triggers a modal alert with a warning message when no forms are available.
 * After the user dismisses the alert, they are redirected to the home page.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertNoForms = () => {
  showAlert({
    text: i18n.t("filterQuery.NoForms"),
    icon: "info",
  }).then(() => {
    window.location.href = "/";
  });
};

/**
 * Displays a SweetAlert notification indicating a successful action in a subsection.
 * This function triggers a modal alert with a success message related to a specific subsection,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertSubSectionSuccess = () => {
  showAlert({
    text: i18n.t("trd.formTittle19"),
  });
};

/**
 * Displays a SweetAlert notification indicating a successful action related to a series.
 * This function triggers a modal alert with a success message for actions related to a series,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertSeriesSuccess = () => {
  showAlert({
    text: i18n.t("trd.formTittle20"),
  });
};

/**
 * Displays a SweetAlert notification indicating a successful action related to a sub-series.
 * This function triggers a modal alert with a success message for actions related to a sub-series,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertSubSeriesSuccess = () => {
  showAlert({
    text: i18n.t("trd.formTittle21"),
  });
};

/**
 * Displays a SweetAlert notification indicating a successful action related to an expedient.
 * This function triggers a modal alert with a success message for actions related to an expedient,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertExpedientSuccess = () => {
  showAlert({
    text: i18n.t("trd.formTittle22"),
  });
};

/**
 * Displays a SweetAlert notification indicating a successful action related to a sub-division.
 * This function triggers a modal alert with a success message for actions related to a sub-division,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertSubDivisionSuccess = () => {
  showAlert({
    text: i18n.t("trd.formTittle23"),
  });
};

/**
 * Displays a SweetAlert notification indicating a successful action related to a directory.
 * This function triggers a modal alert with a success message for actions related to a directory,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertDirectorySuccess = () => {
  showAlert({
    text: i18n.t("trd.formTittle24"),
  });
};

/**
 * Displays a SweetAlert notification indicating a service error with a redirect.
 * This function triggers a modal alert with an error message when there is a failure
 * in submitting data to the server. After the alert is dismissed, the user is redirected
 * to the home page.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertServiceErrorWithRedirect = () => {
  showAlert({
    text: i18n.t("error.data.submit"),
    icon: "error",
  }).then(() => {
    window.location.href = "/";
  });
};

/**
 * Displays a SweetAlert notification indicating a successful creation of a section.
 * This function triggers a modal alert with a success message related to the creation of
 * a section, using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertCrateSectionSuccess = () => {
  showAlert({
    text: i18n.t("trd.formTittle18"),
  });
};

/**
 * Displays a SweetAlert notification indicating a successful record export.
 * This function triggers a modal alert with a success message after records have been exported,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const alertErrorUserTask = () => {
  showAlert({
    text: i18n.t("50016"),
    icon: "error",
  }).then(() => {
    window.location = "/taskBox";
  });
};

/**
 * Displays a warning alert for a completed task and redirects to the task box page.
 * This function triggers an alert using `showAlert` with a warning message indicating that a task has been completed.
 * The message is retrieved from the i18n translation file under the key `taskBoxTableAlertCompletedTask`.
 * After displaying the alert, the user is redirected to the `/taskBox` page.
 * @function
 * @returns {Promise<void>} A promise that resolves when the alert is dismissed and the user is redirected.
 */
export const alertTaskCompleted = () => {
  showAlert({
    text: i18n.t("taskBoxTableAlertCompletedTask"),
    icon: "warning",
  }).then(() => {
    window.location = "/taskBox";
  });
};

/**
 * Displays a warning alert for a disrupted task and redirects to the task box page.
 * This function triggers an alert using `showAlert` with a warning message indicating that a task has been disrupted.
 * The message is retrieved from the i18n translation file under the key `taskBoxTableAlertDisruptedTask`.
 * After displaying the alert, the user is redirected to the `/taskBox` page.
 * @function
 * @returns {Promise<void>} A promise that resolves when the alert is dismissed and the user is redirected.
 */
export const alertTaskDisrupted = () => {
  showAlert({
    text: i18n.t("taskBoxTableAlertDisruptedTask"),
    icon: "warning",
  }).then(() => {
    window.location = "/taskBox";
  });
};

/**
 * Displays an informational alert indicating that there are no tasks available.
 * This function triggers an alert using `showAlert` with an informational message indicating that the task box is empty.
 * The message is retrieved from the i18n translation file under the key `taskBoxTableAlert`.
 * @function
 * @returns {void}
 */
export const showTasksEmpty = () => {
  showAlert({
    text: i18n.t("taskBoxTableAlert"),
    icon: "info",
  });
};

export const showTaskEmptyFilterTask = () => {
  showAlert({
    text: i18n.t("taskFilter.label5"),
    icon: "info",
  });
};

/**
 * Displays a success alert for creating workflow configuration and reloads the page.
 * This function triggers an alert using `showAlert` with a success message indicating that the workflow configuration has been successfully created.
 * The message is retrieved from the i18n translation file under the key `createWorkflow.configurations.isSelectedUser`.
 * After displaying the alert, the page is reloaded.
 * @function
 * @returns {Promise<void>} A promise that resolves when the alert is dismissed and the page is reloaded.
 */
export const showCreateWorkflowConfigSuccess = () => {
  showAlert({
    text: i18n.t("createWorkflow.configurations.isSelectedUser"),
    icon: "success",
  }).then(() => {
    window.location.reload();
  });
};

/**
 * Displays a success alert for selecting a user and reloads the page.
 * This function triggers an alert using `showAlert` with a success message indicating that the user has been successfully selected.
 * The message is retrieved from the i18n translation file under the key `createWorkflow.configurations.isSelectedUser`.
 * After displaying the alert, the page is reloaded.
 * @function
 * @returns {Promise<void>} A promise that resolves when the alert is dismissed and the page is reloaded.
 */
export const showAlertSuccessSenderSelect = () => {
  showAlert({
    text: i18n.t("createWorkflow.configurations.isSelectedUser"),
    icon: "success",
  }).then(() => {
    window.location.reload();
  });
};

/**
 * Displays a success alert for saving reassignment and reloads the page.
 * This function triggers an alert using `showAlert` with a success message indicating that the reassignment has been saved.
 * The message is retrieved from the i18n translation file under the key `integration.EditConfigSalert`.
 * After displaying the alert, the page is reloaded.
 * @function
 * @returns {Promise<void>} A promise that resolves when the alert is dismissed and the page is reloaded.
 */
export const showAlertSaveReassign = () => {
  showAlert({
    text: i18n.t("integration.EditConfigSalert"),
    icon: "success",
  }).then(() => {
    window.location.reload();
  });
};

/**
 * Displays a success alert for deleting a configuration form.
 * This function triggers an alert using `showAlert` with a success message indicating that the configuration form has been successfully deleted.
 * The message is retrieved from the i18n translation file under the key `WorkFlowEdit.deleteOk`.
 * @function
 * @returns {void}
 */
export const handleAlertDeleteConfigForm = () => {
  showAlert({
    text: i18n.t("WorkFlowEdit.deleteOk"),
    icon: "success",
  });
  return;
};

/**
 * Displays a success alert for creating a signature and navigates back in history.
 * This function triggers an alert using `showAlert` with a success message indicating that the signature has been created.
 * The message is retrieved from the i18n translation file under the key `signature.alertDone`.
 * After displaying the alert, it navigates back to the previous page in the browser's history.
 * @function
 * @returns {Promise<void>} A promise that resolves when the alert is dismissed and navigation occurs.
 */
export const showAlertCreateSignature = () => {
  showAlert({
    text: i18n.t("signature.alertDone"),
    icon: "success",
  }).then(function () {
    window.history.back();
  });
};

/**
 * Displays a success alert for exporting records.
 * This function triggers an alert using `showAlert` with a success message indicating that the export of records was successful.
 * The message is retrieved from the i18n translation file under the key `export.records.alert.exporte`.
 * @function
 * @returns {void}
 */
export const showAlertExportRecordsSuccess = () => {
  showAlert({
    text: i18n.t("export.records.alert.exporte"),
  });
};

/**
 * Displays a SweetAlert notification indicating that there is no data to export.
 * This function triggers a modal alert with a warning message when there are no records available
 * for export, using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const showAlertNoExportDataRecord = () => {
  showAlert({
    text: i18n.t("export.records.alert.warining2"),
    icon: "info",
  });
};

/**
 * Displays a SweetAlert notification indicating that the user has no roles assigned.
 * This function triggers a modal alert with an informational message when a user has no roles assigned,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const ShowUserAlertWithoutRoles = () => {
  showAlert({
    text: i18n.t("10024"),
    icon: "info",
  });
};

/**
 * Displays a SweetAlert notification indicating that the max users conected.
 * This function triggers a modal alert with an informational message when the max users conected,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const ShowUserAlertMaxUsers = () => {
  showAlert({
    text: i18n.t("500080"),
    icon: "info",
  });
};

/**
 * Displays a SweetAlert notification indicating a login error.
 * This function triggers a modal alert with an error message when a login attempt fails,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const ShowAlertLoginError = () => {
  showAlert({
    text: i18n.t("modal.Error.body"),
    icon: "error",
  });
};

/**
 * Displays a SweetAlert notification indicating a login error.
 * This function triggers a modal alert with an error message when a login attempt fails,
 * using the SweetAlert library.
 * @function
 * @returns {void} Does not return any value.
 */
export const ShowAlertLoginErrorDecripted = () => {
  showAlert({
    text: i18n.t("modal.Error.body2"),
    icon: "error",
  });
};

/**
 * Displays a SweetAlert notification with a specific message.
 * This function triggers a SweetAlert to show a notification with the text message obtained from the internationalization (i18n) library.
 * The message displayed is retrieved using the key `"recordDetail.AlertFiles"`, which should correspond to a localized string in the application's language files.
 * - The alert does not specify an icon, so it defaults to the standard SweetAlert icon.
 * @function
 */
export const showSweetAlert = () => {
  showAlert({
    text: i18n.t("recordDetail.AlertFiles"),
  });
};
